/**
 * G2eGente - Meus Testes
 * Página que exibe os resultados dos testes DISC e Big Five
 * ou permite realizá-los se ainda não foram feitos
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  Brain,
  Sparkles,
  Target,
  Users,
  Heart,
  Shield,
  ChevronRight,
  CheckCircle2,
  Clock,
  Play,
  Award
} from 'lucide-react';

// DISC Profiles info
const DISC_PROFILES = {
  D: { name: "Dominância", title: "Determinado", icon: Target, color: "text-red-500", bgColor: "bg-red-100" },
  I: { name: "Influência", title: "Inspirador", icon: Users, color: "text-amber-500", bgColor: "bg-amber-100" },
  S: { name: "Estabilidade", title: "Solidário", icon: Heart, color: "text-emerald-500", bgColor: "bg-emerald-100" },
  C: { name: "Conformidade", title: "Consciencioso", icon: Shield, color: "text-blue-500", bgColor: "bg-blue-100" }
};

// G2BP Profiles info (mesmo DISC mas para o teste G2 Behavioral Profile)
const G2BP_PROFILES = {
  D: { name: "Dominância", title: "Determinado", color: "text-red-500", bgColor: "bg-red-100" },
  I: { name: "Influência", title: "Inspirador", color: "text-amber-500", bgColor: "bg-amber-100" },
  S: { name: "Estabilidade", title: "Solidário", color: "text-emerald-500", bgColor: "bg-emerald-100" },
  C: { name: "Conformidade", title: "Consciencioso", color: "text-blue-500", bgColor: "bg-blue-100" }
};

// Big Five traits info
const BIGFIVE_TRAITS = {
  O: { name: "Abertura", color: "bg-purple-500" },
  C: { name: "Conscienciosidade", color: "bg-blue-500" },
  E: { name: "Extroversão", color: "bg-yellow-500" },
  A: { name: "Amabilidade", color: "bg-pink-500" },
  N: { name: "Estabilidade Emocional", color: "bg-green-500" }
};

export default function MeusTestes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await candidatesAPI.getMe();
      setProfile(response.data);
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  const hasDISC = !!profile?.disc_profile;
  const hasBigFive = !!profile?.bigfive_result?.scores;
  const hasG2BP = !!profile?.g2bp_result?.dominant;
  const discDominant = profile?.disc_profile?.charAt(0);
  const discProfile = discDominant ? DISC_PROFILES[discDominant] : null;
  const bigfiveScores = profile?.bigfive_result?.scores;
  const g2bpDominant = profile?.g2bp_result?.dominant;
  const g2bpProfile = g2bpDominant ? G2BP_PROFILES[g2bpDominant] : null;

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-4 pt-4 pb-8 rounded-b-3xl">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/m')} className="text-slate-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold text-white">Meus Testes</h1>
        </div>
        
        <p className="text-slate-400 text-sm">
          Seus resultados de avaliação comportamental
        </p>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* DISC Card */}
        <Card className="border-slate-200 overflow-hidden">
          <div className={`h-2 ${hasDISC ? 'bg-gradient-to-r from-red-500 via-amber-500 to-blue-500' : 'bg-slate-200'}`} />
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${hasDISC ? (discProfile?.bgColor || 'bg-purple-100') : 'bg-slate-100'}`}>
                <Brain className={`w-7 h-7 ${hasDISC ? (discProfile?.color || 'text-purple-600') : 'text-slate-400'}`} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-lg">Teste DISC</h3>
                  {hasDISC ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Concluído
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      Pendente
                    </Badge>
                  )}
                </div>
                
                {hasDISC ? (
                  <>
                    <p className="text-sm text-slate-600 mb-2">
                      Perfil: <span className={`font-semibold ${discProfile?.color}`}>{discProfile?.title}</span>
                      {' '}({profile.disc_profile})
                    </p>
                    {profile.disc_updated_at && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Realizado em {formatDate(profile.disc_updated_at)}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    Descubra seu perfil comportamental através de 20 perguntas
                  </p>
                )}
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/m/disc')}
              className={`w-full mt-4 ${hasDISC ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-cyan-600 hover:bg-cyan-700'}`}
              variant={hasDISC ? "ghost" : "default"}
            >
              {hasDISC ? (
                <>
                  Ver Resultado Completo
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Teste
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* G2 Behavioral Profile Card */}
        <Card className="border-slate-200 overflow-hidden">
          <div className={`h-2 ${hasG2BP ? 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500' : 'bg-slate-200'}`} />
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${hasG2BP ? (g2bpProfile?.bgColor || 'bg-indigo-100') : 'bg-slate-100'}`}>
                <Award className={`w-7 h-7 ${hasG2BP ? (g2bpProfile?.color || 'text-indigo-600') : 'text-slate-400'}`} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-lg">Perfil G2 Comportamental</h3>
                  {hasG2BP ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Concluído
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      Pendente
                    </Badge>
                  )}
                </div>
                
                {hasG2BP ? (
                  <>
                    <p className="text-sm text-slate-600 mb-2">
                      Perfil: <span className={`font-semibold ${g2bpProfile?.color}`}>{g2bpProfile?.title}</span>
                      {' '}({profile.g2bp_result.estilo || profile.g2bp_result.dominant})
                    </p>
                    {profile.g2bp_updated_at && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Realizado em {formatDate(profile.g2bp_updated_at)}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    Teste comportamental exclusivo G2eGente com 40 questões
                  </p>
                )}
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/m/g2bp')}
              className={`w-full mt-4 ${hasG2BP ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              variant={hasG2BP ? "ghost" : "default"}
            >
              {hasG2BP ? (
                <>
                  Ver Resultado Completo
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Teste
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Big Five Card */}
        <Card className="border-slate-200 overflow-hidden">
          <div className={`h-2 ${hasBigFive ? 'bg-gradient-to-r from-purple-500 via-blue-500 to-green-500' : 'bg-slate-200'}`} />
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${hasBigFive ? 'bg-violet-100' : 'bg-slate-100'}`}>
                <Sparkles className={`w-7 h-7 ${hasBigFive ? 'text-violet-600' : 'text-slate-400'}`} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-lg">Big Five (OCEAN)</h3>
                  {hasBigFive ? (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Concluído
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      Pendente
                    </Badge>
                  )}
                </div>
                
                {hasBigFive ? (
                  <>
                    <p className="text-sm text-slate-600 mb-2">
                      Avaliação completa dos 5 traços de personalidade
                    </p>
                    {/* Mini chart */}
                    {bigfiveScores && (
                      <div className="flex gap-1 mt-2">
                        {Object.entries(bigfiveScores).map(([trait, value]) => (
                          <div key={trait} className="flex-1">
                            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${BIGFIVE_TRAITS[trait]?.color || 'bg-slate-400'}`}
                                style={{ width: `${value}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {profile.bigfive_updated_at && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-2">
                        <Clock className="w-3 h-3" />
                        Realizado em {formatDate(profile.bigfive_updated_at)}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    Avaliação de personalidade com 44 ou 60 perguntas
                  </p>
                )}
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/m/bigfive')}
              className={`w-full mt-4 ${hasBigFive ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-violet-600 hover:bg-violet-700'}`}
              variant={hasBigFive ? "ghost" : "default"}
            >
              {hasBigFive ? (
                <>
                  Ver Resultado Completo
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Teste
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-cyan-200 bg-cyan-50">
          <CardContent className="p-4">
            <h4 className="font-semibold text-cyan-900 mb-2">Por que fazer os testes?</h4>
            <ul className="text-sm text-cyan-700 space-y-1">
              <li>• Destaque seu perfil para recrutadores</li>
              <li>• Aumente suas chances em processos seletivos</li>
              <li>• Descubra seus pontos fortes</li>
              <li>• Identifique áreas de desenvolvimento</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
